import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Leaf, TrendingUp, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent, companyInfo } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section with Banner */}
      <section className="relative text-white h-[400px] overflow-hidden">
        {/* Banner Image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_176ab01e-388f-4bea-9c6b-49caaa144cc8/artifacts/r575nxtt_AboutUsBanner.png"
          alt="About Us Banner"
          className="absolute inset-0 w-full h-full object-contain bg-[#1a237e]"
        />
        {/* Light overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
              <Award size={16} className="mr-2" />
              Since {companyInfo.since}
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 drop-shadow-lg">
              Our Story
            </h1>
            <p className="text-xl text-white/90 leading-relaxed drop-shadow">
              A journey of resilience, growth, and commitment to powering mobility.
            </p>
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1a237e] mb-4">
              Our Journey
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From a small 200 sq. ft. space to becoming a leading distributor across West Bengal
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-200" />
            
            {aboutContent.journey.map((milestone, index) => (
              <div
                key={index}
                className={`relative flex items-center mb-12 last:mb-0 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                    <CardContent className="p-6">
                      <span className="inline-block px-3 py-1 bg-[#c41e3a]/10 text-[#c41e3a] rounded-full text-sm font-semibold mb-3">
                        {milestone.year}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {milestone.content}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Timeline Dot */}
                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 items-center justify-center">
                  <div className="w-4 h-4 bg-[#c41e3a] rounded-full border-4 border-white shadow-md" />
                </div>
                
                {/* Empty space for alignment */}
                <div className="hidden md:block w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-[#1a237e] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                {aboutContent.philosophy.title}
              </h2>
              <p className="text-xl text-blue-200 mb-8">
                {aboutContent.philosophy.description}
              </p>
              <div className="space-y-4">
                {aboutContent.philosophy.points.map((point, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-3 h-3 bg-[#c41e3a] rounded-full" />
                    <span className="text-lg">{point}</span>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-blue-200 italic">
                &quot;{aboutContent.philosophy.footer}&quot;
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-colors">
                <TrendingUp className="w-10 h-10 text-[#c41e3a] mb-4" />
                <h3 className="font-semibold text-lg mb-2">Market Development</h3>
                <p className="text-blue-200 text-sm">Building sustainable markets</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-colors">
                <Users className="w-10 h-10 text-orange-400 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Relationships</h3>
                <p className="text-blue-200 text-sm">Long-term partnerships</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-colors">
                <Award className="w-10 h-10 text-yellow-400 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Excellence</h3>
                <p className="text-blue-200 text-sm">Service quality focus</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-colors">
                <Leaf className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Sustainability</h3>
                <p className="text-blue-200 text-sm">Sustainable growth</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1a237e] mb-4">
              What Sets Us Apart
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aboutContent.whatSetsUsApart.map((item, index) => (
              <Card key={index} className="border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <p className="text-gray-700 font-medium">{item}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join a distributor that believes in building markets, not just sales.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-[#c41e3a] hover:bg-[#a01830] text-white px-8 py-6 text-lg">
                Contact Us Today
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg">
                View Our Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
